import React, { useState, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { Lightbox } from './Lightbox';

interface GalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  description?: string;
  source_page_urls?: string;
  original_url?: string;
}

type GalleryTab = 'all' | 'apartment' | 'community';

function classifyPhoto(photo: GalleryImage): 'apartment' | 'community' | null {
  const title = (photo.title || '').toLowerCase().trim();
  if (title.includes('apartment')) return 'apartment';
  if (title.includes('community')) return 'community';
  return null;
}

const TABS: { key: GalleryTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'apartment', label: 'Apartment' },
  { key: 'community', label: 'Community' },
];

export function Gallery() {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<GalleryTab>('all');

  const images = useCollectionBinding<GalleryImage>('gallery.images', {
    collection: 'photo_gallery',
  });

  const galleryImages = images && images.length > 0 ? images : [];

  const classified = useMemo(() => {
    const map = new Map<GalleryImage, 'apartment' | 'community' | null>();
    for (const img of galleryImages) {
      map.set(img, classifyPhoto(img));
    }
    return map;
  }, [galleryImages]);

  const filteredImages = useMemo(() => {
    if (activeTab === 'all') return galleryImages;
    return galleryImages.filter((img) => classified.get(img) === activeTab);
  }, [galleryImages, classified, activeTab]);

  const lightboxImages = useMemo(
    () => filteredImages.map((img) => ({ url: img.image, alt: img.alt_text || img.title || '' })),
    [filteredImages],
  );

  const tabCounts = useMemo(() => {
    let apt = 0;
    let com = 0;
    for (const cat of classified.values()) {
      if (cat === 'apartment') apt++;
      else if (cat === 'community') com++;
    }
    return { all: galleryImages.length, apartment: apt, community: com };
  }, [classified, galleryImages.length]);

  return (
    <section className="py-8 md:py-12 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Tab filters */}
        {galleryImages.length > 0 && (
          <div className="gallery-tabs">
            {TABS.map((tab) => {
              const count = tabCounts[tab.key];
              if (tab.key !== 'all' && count === 0) return null;
              return (
                <button
                  key={tab.key}
                  className={`gallery-tab${activeTab === tab.key ? ' gallery-tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Photo grid */}
        {filteredImages.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {filteredImages.map((img, index) => (
              <div
                key={`${activeTab}-${index}`}
                className="aspect-square overflow-hidden group cursor-pointer gallery-item"
                onClick={() => setSelectedIndex(index)}
              >
                <VibesitesImage
                  url={img.image}
                  alt={img.alt_text || img.title || ''}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Gallery images will be displayed here.</p>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Lightbox
        images={lightboxImages}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onSelect={setSelectedIndex}
      />

      <style>{`
        .gallery-tabs {
          display: flex;
          justify-content: center;
          gap: 0.25rem;
          margin-bottom: 1.5rem;
        }

        .gallery-tab {
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          padding: 0.5rem 1.25rem;
          border: 1px solid var(--vibe-color-border);
          background: transparent;
          color: var(--vibe-color-text-muted);
          cursor: pointer;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
        }
        .gallery-tab:first-child {
          border-radius: 3px 0 0 3px;
        }
        .gallery-tab:last-child {
          border-radius: 0 3px 3px 0;
        }
        .gallery-tab:hover {
          color: var(--vibe-color-text);
          border-color: var(--vibe-color-text-muted);
        }
        .gallery-tab--active {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }
        .gallery-tab--active:hover {
          color: var(--vibe-color-text-on-primary);
          border-color: var(--vibe-color-primary);
        }

        .gallery-item {
          animation: gallery-fade-in 0.35s ease both;
        }

        @keyframes gallery-fade-in {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }

        @media (prefers-reduced-motion: reduce) {
          .gallery-item { animation: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Gallery',
  displayName: 'Photo Gallery',
  tokens: {},
  collectionBindings: {
    'gallery.images': {
      label: 'Gallery Images',
      collection: 'photo_gallery',
    },
  },
};

(Gallery as any).contentSchema = contentSchema;

export default Gallery;
